/**
 * Format utility functions for common formatting operations.
 *
 * Pure logic — uses Intl APIs which are available on both browsers and Hermes
 * (React Native). No DOM dependencies.
 */
import { formatCurrencyWithCode } from '@splitfiesta/shared';

/**
 * Canonical currency formatter used app-wide. Renders the symbol, the
 * amount (with per-currency decimal precision and currency-appropriate
 * grouping), and the ISO code — e.g. "$ 8,167.00 USD", "$ 14.196.701 COP".
 *
 * The ISO code is always shown so amounts are unambiguous across the
 * many currencies that share a symbol (USD/CAD/AUD/MXN/COP all use "$").
 * Delegates to the shared `formatCurrencyWithCode` so web and mobile
 * share a single source of truth. When `locale` is omitted the
 * currency's own locale governs grouping/separators.
 */
export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale?: string
): string {
  return formatCurrencyWithCode(amount, currency, locale);
}

/**
 * The same amount with an explicit `+`/`-` in front of it, for the balance
 * rows that colour the sign rather than spelling out who owes whom. Zero
 * carries no sign.
 */
export function formatSignedCurrency(
  amount: number,
  currency = 'USD',
  locale?: string
): string {
  const sign = amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${sign}${formatCurrency(Math.abs(amount), currency, locale)}`;
}

export function formatAmount(amount: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

export function formatPercentage(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPhoneNumber(phone: string): string {
  const hasPlus = phone.startsWith('+');
  const numbers = phone.replace(/\D/g, '');

  if (numbers.length === 10) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  } else if (numbers.length === 11 && numbers[0] === '1') {
    const prefix = hasPlus ? '+1' : '1';
    return `${prefix} (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7)}`;
  } else if (numbers.length > 11) {
    const countryCode = numbers.slice(0, numbers.length - 10);
    const areaCode = numbers.slice(-10, -7);
    const exchange = numbers.slice(-7, -4);
    const lineNumber = numbers.slice(-4);
    return `+${countryCode} (${areaCode}) ${exchange}-${lineNumber}`;
  }

  return phone;
}

export function truncateText(
  text: string,
  maxLength: number,
  suffix = '...'
): string {
  if (text.length <= maxLength) {
    return text;
  }

  if (maxLength <= 0) {
    return suffix;
  }

  return text.slice(0, maxLength) + suffix;
}

export function capitalizeFirst(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function generateId(length = 12): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function isValidEmail(email: string): boolean {
  if (!email || email.trim() === '') return false;

  const emailRegex =
    /^[a-zA-Z0-9]+([._-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-]?[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
}

export function isValidPassword(password: string): boolean {
  if (password.length < 8) return false;

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

  return hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
}

/**
 * Locale-aware decimal input helpers.
 *
 * Android keyboards enforce the HTML `pattern` attribute — if it only lists ".",
 * the comma key is greyed-out for European users whose locale uses "," as the
 * decimal separator. The constants and helpers below allow both separators at
 * the input level while keeping JS validation and parsing correct.
 */

export const DECIMAL_INPUT_PATTERN = '[0-9.,]*';

export const DECIMAL_INPUT_REGEX = /^\d*[.,]?\d*$/;

export function isValidDecimalInput(value: string): boolean {
  return value === '' || DECIMAL_INPUT_REGEX.test(value);
}

export function normalizeDecimalInput(value: string): number {
  return parseFloat(value.replace(',', '.'));
}

export function sanitizeInput(input: string): string {
  return input
    .replace(/<[^>]*?>/g, '')
    .replace(/</g, '')
    .trim();
}
